import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { ToastContainer, toast } from "react-toastify";
import { Building2, Loader2, Plus, User, Wallet } from "lucide-react";
import PaymentModal from "./Payment/PaymentModal";
import { PaymentContext } from "../contexts/PaymentContext";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import {
  InvoicePFConfig,
  InvoicePJConfig,
  OrganizationSummary,
  PlanInfo,
} from "../shared/Interfaces";
import { OrganizationPermission, hasPermission } from "../shared/organizationPermissions";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";
import { reportHandledError, reportApiError } from "../utils/reportError";

export interface UserConfigs {
  user_has_registered_cards: boolean;
  amount_to_recharge: number;
  min_threshold_to_recharge: number;
  auto_charge_payment_id?: string;
  invoice_type?: string;
  invoice_config?: InvoicePFConfig | InvoicePJConfig;
  active_subscription?: boolean;
}

const INITIAL_USER_CONFIGS: UserConfigs = {
  user_has_registered_cards: false,
  amount_to_recharge: 0,
  min_threshold_to_recharge: 0,
  auto_charge_payment_id: "",
  invoice_type: "",
  invoice_config: undefined,
  active_subscription: false,
};

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-ring focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

export default function Recharge() {
  const {
    paymentStage,
    setPaymentStage,
    setPaymentOption,
    setInvoiceConfig,
    setInvoiceType,
    invoiceConfig,
    invoiceType,
    setAutoChargePaymentId,
    billingScope,
    setBillingScope,
    activeOrganizationId,
    setActiveOrganizationId,
  } = useContext(PaymentContext);

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [userConfigs, setUserConfigs] = useState<UserConfigs>(INITIAL_USER_CONFIGS);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);

  const [accountLimit, setAccountLimit] = useState<string>("");
  const [alertThresholds, setAlertThresholds] = useState<string[]>([""]);
  const [accountSpent, setAccountSpent] = useState<number>(0);
  const [accountPeriod, setAccountPeriod] = useState<string>("");
  const [isSavingAccount, setIsSavingAccount] = useState<boolean>(false);
  const [spendingModalOpen, setSpendingModalOpen] = useState<boolean>(false);
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);

  const [couponCode, setCouponCode] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [plan, setPlan] = useState<PlanInfo>();

  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const accessibleOrganizations = useMemo(() => {
    return organizations.filter((org) => {
      const membership = org.members?.find((member) => member.user_id === currentUserId);
      if (!membership) return false;
      return (
        hasPermission(membership.permissions, OrganizationPermission.BILLING_VIEW_CREDITS) ||
        hasPermission(membership.permissions, OrganizationPermission.BILLING_MANAGE_BILLING)
      );
    });
  }, [organizations, currentUserId]);

  const hasOrganizations = organizations.length > 0;
  const hasAccessibleOrganizations = accessibleOrganizations.length > 0;

  const getPermissionsForOrg = useCallback(
    (orgId: string | undefined | null): string[] => {
      if (!orgId) return [];
      const org = organizations.find((item) => item.organization_id === orgId);
      if (!org) return [];
      const membership = org.members?.find((member) => member.user_id === currentUserId);
      return membership?.permissions ?? [];
    },
    [organizations, currentUserId]
  );

  const currentOrgPermissions = useMemo(() => {
    if (billingScope !== "organization") return [] as string[];
    return getPermissionsForOrg(activeOrganizationId);
  }, [billingScope, activeOrganizationId, getPermissionsForOrg]);

  const canViewCredits =
    billingScope !== "organization" ||
    hasPermission(currentOrgPermissions, OrganizationPermission.BILLING_VIEW_CREDITS);
  const canManageBilling =
    billingScope !== "organization" ||
    hasPermission(currentOrgPermissions, OrganizationPermission.BILLING_MANAGE_BILLING);
  const organizationCreditsLocked = billingScope === "organization" && !canViewCredits;
  const organizationBillingLocked = billingScope === "organization" && !canManageBilling;

  const buildBillingUrl = (
    path: string,
    params: Record<string, string> = {},
    scopeOverride?: "personal" | "organization",
    organizationIdOverride?: string | null
  ) => {
    const scope = scopeOverride ?? billingScope;
    const organizationId = organizationIdOverride ?? activeOrganizationId;
    const search = new URLSearchParams(params);
    if (scope === "organization" && organizationId) {
      search.set("organization_id", organizationId);
    }
    const query = search.toString();
    return `${API_BASE}/api/billing${path}${query ? `?${query}` : ""}`;
  };

  const parseComma = (value: number): string =>
    value.toLocaleString("pt-BR", { useGrouping: false, maximumFractionDigits: 2 });

  const handleSingleButton = () => {
    if (maintenanceMode) {
      toast.warning(
        "Pagamentos estão desabilitados durante a manutenção. Todo o uso via API está gratuito!",
        TOASTPROPS
      );
      return;
    }
    if (billingScope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para realizar a recarga.", TOASTPROPS);
      return;
    }
    if (billingScope === "organization" && !canManageBilling) return;
    setPaymentOption("single");
    setPaymentStage("methods");
  };

  const handleAutoButton = () => {
    if (maintenanceMode) {
      toast.warning(
        "Pagamentos estão desabilitados durante a manutenção. Todo o uso via API está gratuito!",
        TOASTPROPS
      );
      return;
    }
    if (billingScope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para configurar a recarga automática.", TOASTPROPS);
      return;
    }
    if (billingScope === "organization" && !canManageBilling) return;
    setPaymentOption("automatic");
    setPaymentStage("methods");
  };

  const handleCardsButton = () => {
    if (maintenanceMode) {
      toast.warning(
        "Pagamentos estão desabilitados durante a manutenção. Todo o uso via API está gratuito!",
        TOASTPROPS
      );
      return;
    }
    if (billingScope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para gerenciar os cartões.", TOASTPROPS);
      return;
    }
    if (billingScope === "organization" && !canManageBilling) return;
    setPaymentOption("cards-only");
    setPaymentStage("methods");
  };

  const handleInvoiceConfigButton = () => {
    if (billingScope === "organization" && !canManageBilling) return;
    setPaymentStage("invoice-config");
  };

  const fetchBillingConfigs = useCallback(
    async (
      scopeOverride?: "personal" | "organization",
      organizationIdOverride?: string
    ) => {
      const scopeToUse = scopeOverride ?? billingScope;
      const organizationIdToUse = organizationIdOverride ?? activeOrganizationId;

      if (scopeToUse === "organization" && !organizationIdToUse) {
        setUserConfigs(INITIAL_USER_CONFIGS);
        setInvoiceConfig({} as InvoicePFConfig | InvoicePJConfig);
        setInvoiceType("");
        setAutoChargePaymentId("");
        return;
      }
      if (scopeToUse === "organization") {
        const permissions = getPermissionsForOrg(organizationIdToUse);
        if (!hasPermission(permissions, OrganizationPermission.BILLING_MANAGE_BILLING)) {
          setUserConfigs(INITIAL_USER_CONFIGS);
          setInvoiceConfig({} as InvoicePFConfig | InvoicePJConfig);
          setInvoiceType("");
          setAutoChargePaymentId("");
          return;
        }
      }

      try {
        setIsLoadingConfigs(true);
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const endpoint = buildBillingUrl(
          "/user-configs",
          {},
          scopeToUse,
          organizationIdToUse
        );
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            Authorization: `Auth ${access_token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          const data = await response.json();
          const invoiceConfigData = data.invoice_config as
            | InvoicePFConfig
            | InvoicePJConfig
            | undefined;
          setUserConfigs({
            user_has_registered_cards: data.user_has_registered_cards ?? false,
            amount_to_recharge: data.amount_to_recharge ?? 0,
            min_threshold_to_recharge: data.min_threshold_to_recharge ?? 0,
            auto_charge_payment_id: data.auto_charge_payment_id ?? "",
            invoice_type: data.invoice_type ?? "",
            invoice_config: invoiceConfigData,
            active_subscription: data.active_subscription,
          });
          setInvoiceConfig(
            (invoiceConfigData ?? {}) as InvoicePFConfig | InvoicePJConfig
          );
          setInvoiceType(data.invoice_type || "");
          setAutoChargePaymentId(data.auto_charge_payment_id || "");
        } else if (response.status === 403) {
          setUserConfigs(INITIAL_USER_CONFIGS);
          setInvoiceConfig({} as InvoicePFConfig | InvoicePJConfig);
          setInvoiceType("");
          setAutoChargePaymentId("");
        } else {
          reportApiError(response, { feature: "billing.userConfigs", method: "GET" });
          const message =
            scopeToUse === "organization"
              ? "Erro ao recuperar configurações de pagamento da organização."
              : "Erro ao recuperar configurações de recarga.";
          toast.error(message, TOASTPROPS);
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "billing.userConfigs" });
        const message =
          scopeToUse === "organization"
            ? "Erro ao recuperar configurações de pagamento da organização."
            : "Erro ao recuperar configurações de recarga.";
        toast.error(message, TOASTPROPS);
      } finally {
        setIsLoadingConfigs(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      billingScope,
      activeOrganizationId,
      getAccessTokenSilently,
      setInvoiceConfig,
      setInvoiceType,
      setAutoChargePaymentId,
      getPermissionsForOrg,
    ]
  );

  const fetchPlanStatus = useCallback(
    async (
      scopeOverride?: "personal" | "organization",
      organizationIdOverride?: string
    ) => {
      const scopeToUse = scopeOverride ?? billingScope;
      const organizationIdToUse = organizationIdOverride ?? activeOrganizationId;

      if (scopeToUse === "organization" && !organizationIdToUse) {
        setPlan({ credits: 0 });
        return;
      }
      if (scopeToUse === "organization") {
        const permissions = getPermissionsForOrg(organizationIdToUse);
        if (!hasPermission(permissions, OrganizationPermission.BILLING_VIEW_CREDITS)) {
          setPlan({ credits: 0 });
          return;
        }
      }

      try {
        const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!accessToken) return;
        const endpoint = buildBillingUrl("/status", {}, scopeToUse, organizationIdToUse);
        const response = await fetch(endpoint, {
          method: "GET",
          headers: { Authorization: `Auth ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (scopeToUse === "organization") {
            setPlan({
              credits: data.credits ?? 0,
              has_received_initial_credits: data.has_received_initial_credits ?? false,
            });
          } else {
            setPlan({
              plan_status: data.plan_status,
              credits: data.credits ?? 0,
              has_received_initial_credits: data.has_received_initial_credits ?? false,
            });
          }
        } else if (response.status === 403) {
          setPlan({ credits: 0 });
        } else {
          reportApiError(response, { feature: "billing.status", method: "GET" });
          const message =
            scopeToUse === "organization"
              ? "Erro ao recuperar créditos da organização."
              : "Erro ao recuperar status do plano.";
          toast.error(message, TOASTPROPS);
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "billing.status" });
        const message =
          scopeToUse === "organization"
            ? "Erro ao recuperar créditos da organização."
            : "Erro ao recuperar status do plano.";
        toast.error(message, TOASTPROPS);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [billingScope, activeOrganizationId, getAccessTokenSilently, getPermissionsForOrg]
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoadingOrganizations(true);
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data["organizations"] ?? []);
      } else {
        reportApiError(response, { feature: "organizations.list", method: "GET" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.list" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    fetch(`${API_BASE}/api/maintenance-status`)
      .then((res) => res.json())
      .then((data) => setMaintenanceMode(data.maintenance_mode))
      .catch(() => {});
  }, []);

  const fetchAccountSpending = useCallback(async () => {
    try {
      const url =
        billingScope === "organization" && activeOrganizationId
          ? `${API_BASE}/api/organizations/${activeOrganizationId}/account-spending`
          : `${API_BASE}/api/keys/account-spending`;
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAccountLimit(
          data.monthly_account_limit != null ? String(data.monthly_account_limit) : ""
        );
        const thresholds = data.spending_alert_thresholds;
        if (Array.isArray(thresholds) && thresholds.length > 0) {
          setAlertThresholds(thresholds.map((t: number) => String(t)));
        } else {
          setAlertThresholds([""]);
        }
        setAccountSpent(data.monthly_account_spent ?? 0);
        setAccountPeriod(data.monthly_account_period ?? "");
      } else {
        reportApiError(response, { feature: "billing.accountSpending", method: "GET" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "billing.accountSpending" });
      // ignore
    }
  }, [getAccessTokenSilently, billingScope, activeOrganizationId]);

  const handleSaveAccountSpending = async () => {
    const url =
      billingScope === "organization" && activeOrganizationId
        ? `${API_BASE}/api/organizations/${activeOrganizationId}/account-spending`
        : `${API_BASE}/api/keys/account-spending`;
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    const validThresholds = alertThresholds
      .filter((t) => t && parseFloat(t) > 0)
      .map((t) => parseFloat(t));
    const body = {
      monthly_account_limit: accountLimit ? parseFloat(accountLimit) : null,
      spending_alert_thresholds: validThresholds.length > 0 ? validThresholds : null,
    };
    setIsSavingAccount(true);
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: { Authorization: `Auth ${access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        toast.success("Configurações de gastos atualizadas.", TOASTPROPS);
        setSpendingModalOpen(false);
        fetchAccountSpending();
      } else {
        reportApiError(response, { feature: "billing.accountSpending.save", method: "PATCH" });
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Erro ao atualizar configurações.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "billing.accountSpending.save" });
      toast.error("Erro ao atualizar configurações.", TOASTPROPS);
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleDisableSpendingLimit = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja desativar o limite de gastos e os alertas por email?"
      )
    )
      return;
    const url =
      billingScope === "organization" && activeOrganizationId
        ? `${API_BASE}/api/organizations/${activeOrganizationId}/account-spending`
        : `${API_BASE}/api/keys/account-spending`;
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    setIsSavingAccount(true);
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Auth ${access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthly_account_limit: null,
          spending_alert_thresholds: null,
        }),
      });
      if (response.ok) {
        toast.success("Limite de gastos desativado.", TOASTPROPS);
        setAccountLimit("");
        setAlertThresholds([""]);
        fetchAccountSpending();
      } else {
        reportApiError(response, { feature: "billing.accountSpending.disable", method: "PATCH" });
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Erro ao desativar limite.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "billing.accountSpending.disable" });
      toast.error("Erro ao desativar limite.", TOASTPROPS);
    } finally {
      setIsSavingAccount(false);
    }
  };

  useEffect(() => {
    fetchBillingConfigs();
    fetchPlanStatus();
    fetchAccountSpending();
  }, [
    billingScope,
    activeOrganizationId,
    fetchBillingConfigs,
    fetchPlanStatus,
    fetchAccountSpending,
  ]);

  useEffect(() => {
    if (paymentStage === "refresh") {
      fetchBillingConfigs();
      setPaymentStage("");
      fetchPlanStatus();
    }
  }, [paymentStage, fetchBillingConfigs, fetchPlanStatus, setPaymentStage]);

  useEffect(() => {
    if (billingScope === "organization") {
      if (!hasAccessibleOrganizations) {
        setBillingScope("personal");
        setActiveOrganizationId("");
      } else if (
        !accessibleOrganizations.some(
          (org) => org.organization_id === activeOrganizationId
        )
      ) {
        setActiveOrganizationId(accessibleOrganizations[0].organization_id);
      }
    }
  }, [
    billingScope,
    accessibleOrganizations,
    activeOrganizationId,
    setBillingScope,
    setActiveOrganizationId,
    hasAccessibleOrganizations,
  ]);

  const handleDeleteInvoiceConfig = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja remover os dados fiscais? Seus próximos pagamentos serão emitidos como pessoa física não identificada."
      )
    )
      return;
    if (billingScope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para gerenciar os dados fiscais.", TOASTPROPS);
      return;
    }
    if (billingScope === "organization" && !canManageBilling) return;

    try {
      const url = buildBillingUrl("/delete-invoice-config");
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Auth ${access_token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        toast.success("Dados fiscais removidos com sucesso.", TOASTPROPS);
        setInvoiceConfig({} as InvoicePFConfig | InvoicePJConfig);
        setInvoiceType("");
        setUserConfigs((prev) => ({ ...prev, invoice_config: undefined, invoice_type: "" }));
        await fetchBillingConfigs();
      } else {
        reportApiError(response, { feature: "billing.invoiceConfig.delete", method: "POST" });
        toast.error("Erro ao remover dados fiscais.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "billing.invoiceConfig.delete" });
      toast.error("Erro ao remover dados fiscais.", TOASTPROPS);
    }
  };

  const handleDisableAuto = async () => {
    if (billingScope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para gerenciar a recarga automática.", TOASTPROPS);
      return;
    }
    if (billingScope === "organization" && !canManageBilling) return;
    try {
      const url = buildBillingUrl("/config-auto-charge", {
        amount_to_load: "0",
        threshold: "0",
        payment_id: "",
      });
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Auth ${access_token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        toast.success("Recarga automática desativada com sucesso", TOASTPROPS);
        setPaymentStage("refresh");
      } else {
        reportApiError(response, { feature: "recharge.autoRecharge.disable", method: "POST" });
        toast.error("Erro ao desativar recarga automática", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "recharge.autoRecharge.disable" });
      toast.error("Erro ao desativar recarga automática", TOASTPROPS);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) return;
    if (billingScope === "organization" && !canManageBilling) return;
    setIsApplyingCoupon(true);
    try {
      const url = buildBillingUrl("/coupon", { coupon: couponCode });
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Auth ${access_token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === "invalid") {
          toast.error("Cupom inválido. Verifique o código.", TOASTPROPS);
        } else if (data.status === "success") {
          toast.success(
            `Cupom aplicado com sucesso! Você ganhou R$ ${data.amount} de crédito.`,
            TOASTPROPS
          );
          setPaymentStage("refresh");
        } else if (data.status === "coupon_already_used") {
          toast.error("Cupom já foi utilizado.", TOASTPROPS);
        } else if (data.status === "expired") {
          toast.error("Cupom expirado.", TOASTPROPS);
        } else if (data.status === "already_used") {
          toast.error("Cupom já foi utilizado.", TOASTPROPS);
        }
      } else {
        reportApiError(response, { feature: "recharge.coupon", method: "POST" });
        toast.error("Erro ao aplicar o cupom. Verifique o código.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "recharge.coupon" });
      toast.error("Erro ao aplicar o cupom.", TOASTPROPS);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setBillingScope("personal");
      setActiveOrganizationId("");
    } else {
      setBillingScope("organization");
      setActiveOrganizationId(value);
    }
    if (paymentStage && paymentStage !== "" && paymentStage !== "refresh") {
      setPaymentStage("");
    }
  };

  const scopeSelectValue =
    billingScope === "personal" ? "personal" : activeOrganizationId || "";
  const isOrganizationScope = billingScope === "organization";
  const showWelcomeOffer =
    !isOrganizationScope &&
    !isLoadingConfigs &&
    plan !== null &&
    !plan?.has_received_initial_credits;
  const hasInvoiceConfig = !!(
    invoiceType &&
    invoiceConfig &&
    Object.keys(invoiceConfig || {}).length > 0
  );
  const autoChargeActive = userConfigs.amount_to_recharge > 0;

  const getCurrentEntityName = () => {
    if (billingScope === "personal") return "Conta Pessoal";
    if (activeOrganizationId) {
      const org = organizations.find((o) => o.organization_id === activeOrganizationId);
      return org?.name || org?.organization_id || "Organização";
    }
    return "Selecione uma entidade";
  };

  const currentEntityName = getCurrentEntityName();

  const StatusIndicator = ({
    active,
    label,
  }: {
    active: boolean;
    label: string;
  }) => (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
        active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
      )}
    >
      {label}
    </span>
  );

  const ActionCard = ({
    title,
    indicator,
    description,
    locked,
    children,
  }: {
    title: string;
    indicator?: { active: boolean; label: string };
    description: string;
    locked?: boolean;
    children?: React.ReactNode;
  }) => (
    <Card>
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[15px] font-semibold tracking-tight text-foreground">{title}</h3>
          {indicator && <StatusIndicator active={indicator.active} label={indicator.label} />}
        </div>
        <p className="text-[13px] text-muted-foreground">{description}</p>
        {locked ? (
          <p className="rounded-md border border-warning/30 bg-warning/10 p-3 text-[12px] font-medium text-warning">
            Você não possui permissão para visualizar esta parte da organização.
          </p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5">
      {/* Entity badge */}
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
            {isOrganizationScope ? <Building2 className="h-5 w-5" /> : <User className="h-5 w-5" />}
          </span>
          <div className="flex flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {isOrganizationScope ? "Organização ativa" : "Conta pessoal"}
            </span>
            <span className="text-[15px] font-semibold text-foreground">{currentEntityName}</span>
          </div>
        </CardContent>
      </Card>

      {/* Scope selector */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] font-medium text-foreground">Gerenciar créditos de:</span>
        {loadingOrganizations ? (
          <Skeleton className="h-9 w-56" />
        ) : (
          <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
            <SelectTrigger className="min-w-[240px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Minha conta pessoal
                </span>
              </SelectItem>
              {accessibleOrganizations.map((org) => (
                <SelectItem key={org.organization_id} value={org.organization_id}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {org.name || org.organization_id}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {!loadingOrganizations && !hasOrganizations && (
        <p className="text-[12px] text-muted-foreground">
          💡 Dica: Crie uma organização para compartilhar créditos e métodos de pagamento com sua equipe.
        </p>
      )}
      {!loadingOrganizations && hasOrganizations && !hasAccessibleOrganizations && (
        <p className="text-[12px] text-warning">
          ⚠️ Você não possui permissões de gerenciar faturamento em nenhuma organização. Use sua
          conta pessoal para gerenciar créditos.
        </p>
      )}

      {/* Credits overview */}
      <div className={cn("grid gap-4", showWelcomeOffer ? "md:grid-cols-2" : "grid-cols-1")}>
        <Card>
          <CardContent className="flex items-start gap-4 p-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
              <Wallet className="h-6 w-6" />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Saldo atual
              </span>
              {isLoadingConfigs ? (
                <Skeleton className="h-8 w-44" />
              ) : organizationCreditsLocked ? (
                <span className="text-[14px] text-muted-foreground">
                  Você não tem permissão para visualizar o saldo.
                </span>
              ) : (
                <span className="text-3xl font-semibold tracking-tight text-foreground">
                  R$ {parseComma(plan?.credits || 0)}
                </span>
              )}
              {!organizationCreditsLocked && (
                <span className="text-[12px] text-muted-foreground">Créditos nunca expiram</span>
              )}
            </div>
          </CardContent>
        </Card>

        {showWelcomeOffer && (
          <Card>
            <CardContent className="flex items-start gap-4 p-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
                🎁
              </span>
              <div>
                <span className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Oferta de Boas-vindas
                </span>
                <p className="mt-1 text-[14px] text-foreground">
                  Ganhe <strong>R$ 20 de crédito</strong> na primeira recarga ou ao cadastrar um
                  cartão.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Actions grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ActionCard
          title="Recarga Única"
          description="Adicione créditos instantaneamente via cartão ou boleto bancário"
          locked={organizationBillingLocked}
        >
          <div className="mt-auto flex gap-2">
            <Button onClick={handleSingleButton} disabled={isLoadingConfigs} className="flex-1">
              Adicionar créditos
            </Button>
          </div>
        </ActionCard>

        <ActionCard
          title="Recarga Automática"
          indicator={{
            active:
              !isLoadingConfigs && autoChargeActive && !organizationBillingLocked,
            label: isLoadingConfigs
              ? "Carregando"
              : organizationBillingLocked
                ? "Sem acesso"
                : autoChargeActive
                  ? "Ativo"
                  : "Inativo",
          }}
          description="Configure recargas automáticas quando o saldo atingir um limite mínimo"
          locked={organizationBillingLocked}
        >
          <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Valor da recarga
              </p>
              {isLoadingConfigs ? (
                <Skeleton className="mt-1 h-4 w-20" />
              ) : (
                <p className="mt-1 text-[13px] font-semibold text-foreground">
                  {autoChargeActive
                    ? `R$ ${parseComma(userConfigs.amount_to_recharge)}`
                    : "Não configurado"}
                </p>
              )}
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Limite mínimo
              </p>
              {isLoadingConfigs ? (
                <Skeleton className="mt-1 h-4 w-20" />
              ) : (
                <p className="mt-1 text-[13px] font-semibold text-foreground">
                  {userConfigs.min_threshold_to_recharge > 0
                    ? `R$ ${parseComma(userConfigs.min_threshold_to_recharge)}`
                    : "Não configurado"}
                </p>
              )}
            </div>
          </div>
          <div className="mt-auto flex gap-2">
            <Button onClick={handleAutoButton} disabled={isLoadingConfigs} className="flex-1">
              {autoChargeActive ? "Reconfigurar" : "Configurar"}
            </Button>
            {autoChargeActive && (
              <Button
                variant="outline"
                onClick={handleDisableAuto}
                disabled={isLoadingConfigs}
              >
                Desativar
              </Button>
            )}
          </div>
        </ActionCard>

        <ActionCard
          title="Cartões"
          description="Gerencie seus cartões de crédito salvos"
          locked={organizationBillingLocked}
        >
          <div className="mt-auto flex gap-2">
            <Button
              onClick={handleCardsButton}
              disabled={isLoadingConfigs}
              className="flex-1"
            >
              Gerenciar
            </Button>
          </div>
        </ActionCard>

        <ActionCard
          title="Dados fiscais"
          indicator={{
            active: !isLoadingConfigs && hasInvoiceConfig && !organizationBillingLocked,
            label: isLoadingConfigs
              ? "Carregando"
              : organizationBillingLocked
                ? "Sem acesso"
                : hasInvoiceConfig
                  ? "Configurado"
                  : "Não configurado",
          }}
          description="Configure informações para emissão de notas fiscais"
          locked={organizationBillingLocked}
        >
          <div className="mt-auto flex gap-2">
            <Button
              onClick={handleInvoiceConfigButton}
              disabled={isLoadingConfigs}
              className="flex-1"
            >
              Configurar
            </Button>
            {hasInvoiceConfig && (
              <Button
                variant="outline"
                onClick={handleDeleteInvoiceConfig}
                disabled={isLoadingConfigs}
              >
                Remover
              </Button>
            )}
          </div>
        </ActionCard>

        <ActionCard
          title="Cupom promocional"
          description="Resgate créditos com códigos promocionais"
          locked={organizationBillingLocked}
        >
          <div className="mt-auto flex gap-2">
            <input
              type="text"
              placeholder="Digite o código"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              className={cn(inputClass, "flex-1")}
            />
            <Button
              onClick={handleApplyCoupon}
              disabled={isApplyingCoupon || isLoadingConfigs}
            >
              {isApplyingCoupon ? "Aplicando..." : "Aplicar"}
            </Button>
          </div>
        </ActionCard>

        <ActionCard
          title="Controle de gastos"
          indicator={{
            active: !isLoadingConfigs && !!accountLimit && !organizationBillingLocked,
            label: isLoadingConfigs
              ? "Carregando"
              : organizationBillingLocked
                ? "Sem acesso"
                : accountLimit
                  ? "Ativo"
                  : "Inativo",
          }}
          description="Defina limites mensais de gastos e alertas por email para controlar o uso da API"
          locked={organizationBillingLocked}
        >
          <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Limite mensal
              </p>
              {isLoadingConfigs ? (
                <Skeleton className="mt-1 h-4 w-20" />
              ) : (
                <p className="mt-1 text-[13px] font-semibold text-foreground">
                  {accountLimit
                    ? `R$ ${parseComma(parseFloat(accountLimit))}`
                    : "Sem limite"}
                </p>
              )}
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Gasto atual
              </p>
              {isLoadingConfigs ? (
                <Skeleton className="mt-1 h-4 w-20" />
              ) : (
                <p className="mt-1 text-[13px] font-semibold text-foreground">
                  R$ {accountSpent.toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <div className="mt-auto flex gap-2">
            <Button
              onClick={() => setSpendingModalOpen(true)}
              disabled={isLoadingConfigs}
              className="flex-1"
            >
              Configurar
            </Button>
            {!!accountLimit && (
              <Button
                variant="outline"
                onClick={handleDisableSpendingLimit}
                disabled={isLoadingConfigs || isSavingAccount}
              >
                Desativar
              </Button>
            )}
          </div>
        </ActionCard>
      </div>

      <Dialog open={spendingModalOpen} onOpenChange={(o) => !o && setSpendingModalOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Controle de gastos</DialogTitle>
            <DialogDescription>
              Gasto mensal atual:{" "}
              <strong className="text-foreground">R$ {accountSpent.toFixed(2)}</strong>
              {accountPeriod && <span> ({accountPeriod})</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-foreground">
                Limite mensal de gastos (R$)
              </label>
              <p className="text-[12px] text-muted-foreground">
                Ao atingir este valor, novas requisições via API serão bloqueadas até o próximo
                mês. Para remover o limite, apague o valor e salve.
              </p>
              <input
                type="number"
                value={accountLimit}
                onChange={(e) => setAccountLimit(e.target.value)}
                placeholder="Sem limite"
                min="0"
                step="0.01"
                disabled={isSavingAccount}
                className={inputClass}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-foreground">
                Alertas de gasto por email (R$)
              </label>
              <p className="text-[12px] text-muted-foreground">
                Receba um email quando seu gasto mensal atingir cada um destes valores.
              </p>
              {alertThresholds.map((threshold, idx) => (
                <div className="flex gap-2" key={idx}>
                  <input
                    type="number"
                    value={threshold}
                    onChange={(e) => {
                      const updated = [...alertThresholds];
                      updated[idx] = e.target.value;
                      setAlertThresholds(updated);
                    }}
                    placeholder="Ex: 50.00"
                    min="0"
                    step="0.01"
                    disabled={isSavingAccount}
                    className={cn(inputClass, "flex-1")}
                  />
                  {alertThresholds.length > 1 && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setAlertThresholds(alertThresholds.filter((_, i) => i !== idx))
                      }
                      disabled={isSavingAccount}
                    >
                      ✕
                    </Button>
                  )}
                </div>
              ))}
              {alertThresholds.length < 5 && (
                <Button
                  variant="outline"
                  onClick={() => setAlertThresholds([...alertThresholds, ""])}
                  disabled={isSavingAccount}
                  className="gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar alerta
                </Button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleSaveAccountSpending} disabled={isSavingAccount} className="w-full gap-2">
              {isSavingAccount && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSavingAccount ? "Salvando..." : "Salvar configurações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentModal />
      <ToastContainer />
    </div>
  );
}
